import React, { useMemo } from "react"

import { Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"

import Button from "../../../../../../Components/Button"
import InputNumber from "../../../../../../Components/Input/InputNumber"
import Select from "../../../../../../Components/Select"
import Icon from "../../../../../../Icon"
import {
  CATEGORY_LABELS,
  ISSUER_OPTIONS,
  NETWORK_OPTIONS,
  OP_LABELS,
  PROCESSOR_OPTIONS,
  TYPE_OPTIONS
} from "../../utils/ruleSummary"

import styles from "./conditionsBuilder.module.less"

const { Text } = Typography

// The condition fields, as data so adding one is a single entry. Amount is a numeric
// threshold; the rest are single-select matches. Category options come from the API.
// Payment processor is only offered where relevant (assignment rules), via includeProcessor.
// Card network + Issuer are offered only where relevant (assignment rules), via
// includeNetworkIssuer; the never-auto-submit rules match on category/amount only.
const buildFields = (categoryOptions, includeProcessor, includeNetworkIssuer, includeType) => {
  const fields = [
    // Inclusive bounds only: "$500 and up" is what merchants mean, so they should not
    // have to type 499.99 to include 500. Legacy gt/lt rules still render (see OP_LABELS).
    { value: "amount", label: "Amount", kind: "number", ops: ["gte", "lte"] },
    {
      value: "category",
      label: "Category",
      kind: "select",
      ops: ["is", "is_not"],
      options: categoryOptions,
      catLabels: true
    }
  ]
  if (includeProcessor) {
    fields.push({
      value: "payment_processor",
      label: "Payment processor",
      kind: "select",
      ops: ["is", "is_not"],
      options: PROCESSOR_OPTIONS
    })
  }
  if (includeType) {
    fields.push({
      value: "dispute_type",
      label: "Type",
      kind: "select",
      ops: ["is", "is_not"],
      options: TYPE_OPTIONS
    })
  }
  if (includeNetworkIssuer) {
    fields.push(
      {
        value: "card_network",
        label: "Card network",
        kind: "select",
        ops: ["is", "is_not"],
        options: NETWORK_OPTIONS
      },
      {
        value: "issuer",
        label: "Issuer",
        kind: "select",
        ops: ["is", "is_not"],
        options: ISSUER_OPTIONS
      }
    )
  }
  return fields
}

// The [field][operator][value] condition rows shared by the approval-rule and
// assignment-rule modals. Controlled: `conditions` in, `onChange(next)` out.
const ConditionsBuilder = ({
  conditions,
  onChange,
  categoryOptions = [],
  includeProcessor = false,
  includeNetworkIssuer = true,
  // Case type (Dispute / Inquiry) is offered only where relevant (assignment rules).
  includeType = false
}) => {
  const { t } = useTranslation()
  const FIELDS = useMemo(
    () => buildFields(categoryOptions, includeProcessor, includeNetworkIssuer, includeType),
    [categoryOptions, includeProcessor, includeNetworkIssuer, includeType]
  )
  const fieldDef = (f) => FIELDS.find((x) => x.value === f) || FIELDS[0]

  const add = () => onChange([...conditions, { field: "amount", op: "gte", value: "" }])
  const remove = (i) => onChange(conditions.filter((_, idx) => idx !== i))
  const change = (i, patch) =>
    onChange(
      conditions.map((row, idx) => {
        if (idx !== i) return row
        const next = { ...row, ...patch }
        if (patch.field && patch.field !== row.field) {
          const fd = fieldDef(patch.field)
          next.op = fd.ops[0]
          next.value = ""
        }
        return next
      })
    )

  const catOption = (o) => ({ value: o.value, label: t(CATEGORY_LABELS[o.value] || o.label) })

  return (
    <Flex vertical gap={8}>
      {conditions.map((cond, i) => {
        const fd = fieldDef(cond.field)
        return (
          <React.Fragment key={i}>
            {/* All conditions in a rule must match, so join the rows with an explicit AND. */}
            {i > 0 ? <Text className={styles.and}>{t("AND")}</Text> : null}
            <Flex gap={8} align="center" wrap="wrap" className={styles.condRow}>
              <Select
                value={cond.field}
                options={FIELDS.map((f) => ({ value: f.value, label: t(f.label) }))}
                onChange={(v) => change(i, { field: v })}
                popupMatchSelectWidth={false}
                style={{ width: 150 }}
              />
              <Select
                value={cond.op}
                options={fd.ops.map((op) => ({ value: op, label: t(OP_LABELS[op]) }))}
                onChange={(v) => change(i, { op: v })}
                popupMatchSelectWidth={false}
                style={{ width: 140 }}
              />
              {fd.kind === "number" ? (
                <InputNumber
                  addonBefore={<Text type="secondary">$</Text>}
                  value={cond.value === "" ? undefined : cond.value}
                  min={0}
                  step={10}
                  precision={2}
                  onChange={(v) => change(i, { value: Number(v) || 0 })}
                  style={{ flex: 1, minWidth: 120 }}
                />
              ) : (
                <Select
                  value={cond.value || undefined}
                  placeholder={t("Select...")}
                  options={
                    fd.catLabels
                      ? (fd.options || []).map(catOption)
                      : (fd.options || []).map((o) => ({ value: o.value, label: t(o.label) }))
                  }
                  onChange={(v) => change(i, { value: v })}
                  // Long category labels (e.g. "Merchandise / Services Not Received") were
                  // truncated in both the field and the dropdown. Let the menu size to its
                  // content and give the field a wider floor so the selection is readable.
                  popupMatchSelectWidth={false}
                  style={{ flex: 1, minWidth: 200 }}
                />
              )}
              <Button
                type="text"
                aria-label={t("Remove condition")}
                icon={<Icon type="delete" />}
                disabled={conditions.length <= 1}
                onClick={() => remove(i)}
              />
            </Flex>
          </React.Fragment>
        )
      })}
      <Button
        type="default"
        icon={<Icon type="plus" />}
        onClick={add}
        text={t("Add condition")}
        style={{ alignSelf: "flex-start" }}
      />
    </Flex>
  )
}

export default ConditionsBuilder
